//Vignesh - Added Free Gift logics, Recommendations Logics, slabs and animation implementation
class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    this.isProcessingGifts = false;
    this.shouldSkipGiftCheck = false;
    this.initialGiftCheckDone = false; // NEW: Track if initial check completed

    // (Vignesh) set of gift variant ids currently present (used to block UI interactions)
    this._giftVariantIds = new Set();
    // delegated click handler bound reference
    this._delegatedClickHandler = this._preventGiftControlClicks.bind(this);
    this._delegatedKeydownHandler = this._preventGiftQuantityKeydown.bind(this);

    // --- New for Option B (user-interaction-based disabling) ---
    // Bound handlers for user edits
    this._userEditClickHandler = this._onUserEditClick.bind(this);
    this._userEditInputHandler = this._onUserEditInput.bind(this);
    // Allow re-enable when cart render completes
    this._onCartRenderedHandler = this._onCartRendered.bind(this);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
      return;
    }

    this.init();

    // NEW: Do initial gift check after a short delay to ensure cart is loaded
    setTimeout(() => {
      this.handleFreeGifts().then(() => {
        this.initialGiftCheckDone = true;
      });
    }, 500);

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
      // Check gifts after any cart update
      if (!this.shouldSkipGiftCheck) {
        this.handleFreeGifts();
      }
      // NOTE: Do NOT automatically re-enable checkout here. We re-enable when the cart has finished rendering
      // (cart:updated-and-rendered) to ensure UI is consistent after async updates.
    });

    // Listen for the global event that indicates render finished, so we can re-enable checkout
    document.addEventListener('cart:updated-and-rendered', this._onCartRenderedHandler, true);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    // remove delegated handlers
    document.removeEventListener('click', this._delegatedClickHandler, true);
    document.removeEventListener('keydown', this._delegatedKeydownHandler, true);

    // remove user edit handlers
    document.removeEventListener('click', this._userEditClickHandler, true);
    document.removeEventListener('input', this._userEditInputHandler, true);
    document.removeEventListener('change', this._userEditInputHandler, true);

    // remove cart rendered handler
    document.removeEventListener('cart:updated-and-rendered', this._onCartRenderedHandler, true);
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))

    // Delegated handlers to prevent user interacting with free-gift controls:
    // use capture phase so we can block before theme handlers run.
    document.addEventListener('click', this._delegatedClickHandler, true);
    document.addEventListener('keydown', this._delegatedKeydownHandler, true);

    // --- New (Option B): delegated listeners to detect user-initiated cart edits ---
    // We attach these in capture phase so we catch them before theme handlers.
    document.addEventListener('click', this._userEditClickHandler, true);
    // quantity inputs might be edited via typing or blur/change — handle 'input' and 'change'
    document.addEventListener('input', this._userEditInputHandler, true);
    document.addEventListener('change', this._userEditInputHandler, true);
  }

  // INSIDE CartDrawerSection CLASS

  /**
   * Triggers the emoji confetti/particle effect when a milestone is reached.
   * NOTE: This is NOT the simple pop animation. It creates new emoji elements.
   */
  _triggerAnimations() {
    const reachedThresholds = this.querySelectorAll('.shipping_bar_threshold[data-reached="true"]');
    
    if (reachedThresholds.length === 0) return;

    reachedThresholds.forEach((threshold, index) => {
      
      // Check if the confetti has ALREADY run for this threshold.
      const confettiHasRan = threshold.getAttribute('data-confetti-ran') === 'true';

      // 🔥 NEW LOGIC: Trigger confetti if:
      // 1. It is the FIRST SLAB (index 0) AND the confetti hasn't run yet.
      // 2. It is ANY OTHER SLAB (index > 0) AND the confetti hasn't run yet.
      if (!confettiHasRan) {
        
        // Get the milestone marker's position
        const rect = threshold.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2; 

        // Trigger pop animation (confetti effect) at the milestone's position
        for (let i = 0; i < 30; i++) {
          this._createParticle(x, y);
        }
        
        // Mark as animated to prevent immediate re-run on subsequent renders
        threshold.setAttribute('data-confetti-ran', 'true');
        
        // Remove the flag after a delay so it can run again if the cart is edited later
        setTimeout(() => {
          threshold.removeAttribute('data-confetti-ran');
        }, 4000); 
      }
    });
  }

  /**
   * Creates and animates a single emoji particle using WAAPI.
   * @param {number} x - The horizontal starting position (viewport x-coordinate).
   * @param {number} y - The vertical starting position (viewport y-coordinate).
   */
  _createParticle(x, y) {
      const particle = document.createElement('span');
      document.body.appendChild(particle);

      const emojis = ['🎉', '🥳', '✨', '🎈', '🎊', '💫', '🎁', '🚚'];
      particle.innerHTML = emojis[Math.floor(Math.random() * emojis.length)];
      particle.style.fontSize = `${Math.random() * 24 + 16}px`;

      // 1. Mandatory base styles for positioning the particle on the screen
      particle.style.position = 'fixed';
      particle.style.left = `${x}px`;
      particle.style.top = `${y}px`;
      particle.style.zIndex = '9999';
      particle.style.pointerEvents = 'none'; // Don't block clicks on elements behind it

      // 2. Define the animation keyframes
      // Keyframes MUST use 'translate(-50%, -50%)' to center the particle on the (x, y) coordinates.
      const popKeyframes = [
          { 
              // Start: Centered on (x, y), scale 1, no scatter
              transform: `translate(-50%, -50%) scale(1) rotate(0deg)`,
              opacity: 1
          },
          { 
              // End: Scatter away and fade out
              transform: `translate(-50%, -50%) translate(${(Math.random() - 0.5) * 300}px, ${(Math.random() - 0.5) * 300}px) scale(0.5) rotate(${Math.random() * 720}deg)`,
              opacity: 0
          }
      ];

      // 3. Define animation options
      const popOptions = {
          duration: Math.random() * 1000 + 2500, // 2.5s to 3.5s duration
          easing: 'cubic-bezier(0, .9, .57, 1)', // Bouncy easing
          delay: Math.random() * 100, // Staggered start delay
          iterations: 1,
          fill: 'forwards'
      };

      // 4. Start the animation
      const animation = particle.animate(popKeyframes, popOptions);

      // 5. Remove the element when the animation finishes
      animation.onfinish = () => {
          particle.remove();
      };
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector,
        );
      }
    });

    this._triggerAnimations();
    // After render, apply UI restrictions for gifts
    // Fetch latest cart and apply UI protections (non-blocking)
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => this.applyGiftUiRestrictions(cart))
      .catch(() => { /* ignore */ });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
    // Dispatch event so external scripts (like recommendations) know to update
    document.dispatchEvent(new CustomEvent('cart:updated-and-rendered', { bubbles: true }));

    // --- Re-enable checkout after the cart has been rendered and UI protections applied.
    // We don't await the fetch above (it's non-blocking), but the event consumers should be aware.
    // Also ensure we re-enable here as well (defensive).
    this._enableCheckoutButton();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  // Make this async so callers can await refresh if needed
  async refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    try {
      const response = await fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`);
      const json = await response.json();
      const parsedState = {
        "sections": json
      };
      this.renderContents(parsedState, false);

      // After rendering, fetch cart to update gift UI protections (and update giftVariantIds)
      const cart = await fetch('/cart.js').then(res => res.json());
      this.applyGiftUiRestrictions(cart);

      // 🔥 ADDED: Explicitly run gift check after the drawer is refreshed/re-rendered
      await this.handleFreeGifts(true); 
      
      // Kept: This event triggers the recommendation list to refresh its data
      document.dispatchEvent(new CustomEvent('cart:updated-and-rendered', { bubbles: true }));

    } catch (err) {
      console.log(err);
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Main gift engine
   * - Uses slab2Min/slab2Max and slab3Min from window.freeGiftConfig
   * - Ensures only the correct slab gift is present
   * - Removes gifts if cart contains only free gifts
   */
  async handleFreeGifts(forceCheck = false) { // 👈 MODIFIED: Added parameter
    if (this.isProcessingGifts && !forceCheck) return; // 👈 MODIFIED: Check forceCheck
    this.isProcessingGifts = true;

    try {
      const cart = await fetch('/cart.js').then(res => res.json());
      const cartValue = cart.total_price / 100;
      const config = window.freeGiftConfig || {};

      const isGiftItem = (item) =>
        item.properties && item.properties._free_gift === 'true';

      // Current gifts and paid items
      const currentGiftItems = cart.items.filter(isGiftItem);
      const paidItems = cart.items.filter((it) => !isGiftItem(it));
      let needsRefresh = false;
      let giftWasAdded = false;

      // If the cart only contains free gifts -> remove them (can't have gifts without paid items)
      if (paidItems.length === 0 && currentGiftItems.length > 0) {
        this.shouldSkipGiftCheck = true;
        for (const gift of currentGiftItems) {
          await this.removeGiftFromCart(gift.variant_id);
        }
        this.shouldSkipGiftCheck = false;
        needsRefresh = true;
      } else {
        // Parse config numbers defensively
        const slab2Min = Number(config.slab2Min);
        const slab2Max = Number(config.slab2Max);
        const slab3Min = Number(config.slab3Min);

        // Determine which slab should be present (mutually exclusive)
        // slab3 takes precedence if cartValue >= slab3Min
        const shouldHaveSlab3 =
          config.slab3Product != null &&
          !Number.isNaN(slab3Min) &&
          cartValue >= slab3Min;

        const shouldHaveSlab2 =
          config.slab2Product != null &&
          !Number.isNaN(slab2Min) &&
          !Number.isNaN(slab2Max) &&
          cartValue >= slab2Min &&
          cartValue <= slab2Max &&
          !shouldHaveSlab3; // ensure exclusivity: if slab3 should be present, slab2 is not

        const hasSlab2Gift = currentGiftItems.some(
          (item) => item.variant_id == config.slab2Product,
        );
        const hasSlab3Gift = currentGiftItems.some(
          (item) => item.variant_id == config.slab3Product,
        );

        // If both gifts are present (somehow), keep only the correct one
        if (hasSlab2Gift && hasSlab3Gift) {
          if (shouldHaveSlab3 && !shouldHaveSlab2) {
            this.shouldSkipGiftCheck = true;
            await this.removeGiftFromCart(config.slab2Product);
            this.shouldSkipGiftCheck = false;
            needsRefresh = true;
          } else if (shouldHaveSlab2 && !shouldHaveSlab3) {
            this.shouldSkipGiftCheck = true;
            await this.removeGiftFromCart(config.slab3Product);
            this.shouldSkipGiftCheck = false;
            needsRefresh = true;
          } else {
            // Neither slab should be present (or config mismatch) -> remove both
            this.shouldSkipGiftCheck = true;
            await this.removeGiftFromCart(config.slab2Product);
            await this.removeGiftFromCart(config.slab3Product);
            this.shouldSkipGiftCheck = false;
            needsRefresh = true;
          }
        } else {
          // Add/remove based on expected slabs
          if (shouldHaveSlab3 && !hasSlab3Gift) {
            this.shouldSkipGiftCheck = true;
            await this.addGiftToCart(config.slab3Product);
            this.shouldSkipGiftCheck = false;
            needsRefresh = true;
            giftWasAdded = true; // NEW

            // If slab2 was present, remove it (ensure only single slab)
            if (hasSlab2Gift) {
              this.shouldSkipGiftCheck = true;
              await this.removeGiftFromCart(config.slab2Product);
              this.shouldSkipGiftCheck = false;
              needsRefresh = true;
            }
          }

          if (shouldHaveSlab2 && !hasSlab2Gift) {
            this.shouldSkipGiftCheck = true;
            await this.addGiftToCart(config.slab2Product);
            this.shouldSkipGiftCheck = false;
            needsRefresh = true;
            giftWasAdded = true; // NEW

            // If slab3 was present, remove it
            if (hasSlab3Gift) {
              this.shouldSkipGiftCheck = true;
              await this.removeGiftFromCart(config.slab3Product);
              this.shouldSkipGiftCheck = false;
              needsRefresh = true;
            }
          }

          // If neither slab should be present but a slab gift exists -> remove them
          if (!shouldHaveSlab2 && !shouldHaveSlab3 && (hasSlab2Gift || hasSlab3Gift)) {
            this.shouldSkipGiftCheck = true;
            if (hasSlab2Gift) await this.removeGiftFromCart(config.slab2Product);
            if (hasSlab3Gift) await this.removeGiftFromCart(config.slab3Product);
            this.shouldSkipGiftCheck = false;
            needsRefresh = true;
          }
        }
      }

      if (needsRefresh) {
        await this.refreshCartDrawer();
        
        // NEW: If a gift was added and drawer is closed, open it to show the gift
        // Only do this after initial check is done (not on page load)
        if (giftWasAdded && !this.isOpen && this.initialGiftCheckDone) {
          setTimeout(() => {
            this.toggleDrawerClasses();
          }, 300);
        }
      }
    } catch (error) {
      console.error('Error handling free gifts:', error);
    } finally {
      this.isProcessingGifts = false;
      // ensure flag reset
      this.shouldSkipGiftCheck = false;
    }
  }

  async addGiftToCart(variantId) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { '_free_gift': 'true' }
        })
      });
      
      // NEW: Check if add was successful
      if (!response.ok) {
        throw new Error(`Failed to add gift: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error adding free gift:', error);
      throw error; // Re-throw so caller knows it failed
    }
  }

  /**
   * Remove gift by variant id — uses the cart item key to call change.js
   * (more reliable than passing variant id to change.js).
   */
  async removeGiftFromCart(variantId) {
    try {
      const cart = await fetch('/cart.js').then(res => res.json());
      // find the gift line (ensure it is a free gift)
      const line = cart.items.find(item =>
        item.variant_id == variantId && item.properties && item.properties._free_gift === 'true'
      );

      if (!line) {
        // fallback: try by variant id using change.js with variant id (less reliable)
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 0
          })
        });
        return;
      }

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: line.key,
          quantity: 0
        })
      });
    } catch (error) {
      console.error('Error removing free gift:', error);
    }
  }

  /**
   * Apply UI restrictions so that free gifts cannot be deleted or have quantity changed.
   * Uses a set of gift variant ids (from cart) to hide/disable controls and to block interactions.
   *
   * Note: selectors are reasonably broad — if your theme uses different classes/data-attributes
   * you might need to tweak the query selectors below.
   */
  applyGiftUiRestrictions(cart) {
    try {
      const isGiftItem = (item) =>
        item.properties && item.properties._free_gift === 'true';
      const giftItems = (cart && cart.items) ? cart.items.filter(isGiftItem) : [];
      // update internal set
      this._giftVariantIds = new Set(giftItems.map(i => String(i.variant_id)));

      // generic container selectors where a cart line might live:
      const rowSelectors = [
        '.cart-item',
        '.cart__row',
        '.cart__line',
        '.cart-item-row',
        '[data-line-item]',
        '[data-cart-item]',
        '.cart__item'
      ].join(',');

      // For each gift variant, find a matching element and disable/hide controls inside it
      this._giftVariantIds.forEach((variantId) => {
        // left many possible attribute names to be robust across themes
        const variantSelectors = [
          `[data-variant-id="${variantId}"]`,
          `[data-product-variant-id="${variantId}"]`,
          `[data-variant="${variantId}"]`,
          `[data-line-variant="${variantId}"]`,
          `[data-variantid="${variantId}"]`
        ].join(',');

        // find nodes that identify the line
        const nodes = Array.from(document.querySelectorAll(variantSelectors));
        if (!nodes.length) {
          // try to find by inputs (some themes put the variant id into inputs)
          const inputs = Array.from(document.querySelectorAll(`input[name="id"][value="${variantId}"], input[value="${variantId}"]`));
          nodes.push(...inputs);
        }

        nodes.forEach((node) => {
          const lineContainer = node.closest(rowSelectors) || node.closest('li') || node.closest('tr') || node.parentElement;
          if (!lineContainer) return;

          // hide/disable any remove links/buttons
          const removeSelectors = [
            '.cart__remove',
            '.js-remove',
            '.cart-item__remove',
            '.remove',
            'button[data-remove-line]',
            'a[href*="/cart/change"]',
            '.cart__remove-item'
          ].join(',');
          lineContainer.querySelectorAll(removeSelectors).forEach((btn) => {
            try {
              btn.setAttribute('aria-hidden', 'true');
              btn.style.pointerEvents = 'none';
              btn.style.opacity = '0';
              btn.disabled = true;
            } catch (e) { /* ignore styling errors */ }
          });

          // disable quantity inputs and plus/minus controls
          // attempt many common selectors
          const qtyInputs = Array.from(lineContainer.querySelectorAll('input[type="number"], input.qty, input.quantity, .qty-input, input[name="updates[]"]'));
          qtyInputs.forEach((input) => {
            try {
              // Force quantity to 1 and disable editing
              input.value = 1;
              input.setAttribute('readonly', 'true');
              input.setAttribute('aria-disabled', 'true');
              input.disabled = true;
            } catch (e) {}
          });

          const qtyButtonSelectors = [
            '.quantity-increment',
            '.quantity-decrement',
            '.cart__qty-plus',
            '.cart__qty-minus',
            '.qty-btn',
            '.qty-plus',
            '.qty-minus'
          ].join(',');
          lineContainer.querySelectorAll(qtyButtonSelectors).forEach(btn => {
            try {
              btn.disabled = true;
              btn.setAttribute('aria-disabled', 'true');
              btn.style.pointerEvents = 'none';
              btn.style.opacity = '0';
            } catch (e) {}
          });

          // Also add data attribute to the container so delegated handlers can detect it quickly
          try {
            lineContainer.setAttribute('data-free-gift', 'true');
            // store variant id on container for easy detection
            lineContainer.setAttribute('data-free-gift-variant', variantId);
          } catch (e) {}
        });
      });

      // Clean up any previously flagged containers that are no longer gifts
      Array.from(document.querySelectorAll('[data-free-gift]')).forEach(container => {
        const vid = container.getAttribute('data-free-gift-variant');
        if (vid && !this._giftVariantIds.has(String(vid))) {
          container.removeAttribute('data-free-gift');
          container.removeAttribute('data-free-gift-variant');
          // Note: we don't try to re-enable UI elements since a full drawer refresh will re-render them.
        }
      });

    } catch (err) {
      console.error('Failed to apply gift UI protections', err);
    }
  }

  /**
   * Delegated click handler (capture phase) to block attempts to remove or change free gifts.
   * This is defensive: it prevents theme-level handlers from firing for gift controls.
   */
  _preventGiftControlClicks(e) {
    if (!this._giftVariantIds || this._giftVariantIds.size === 0) return;

    // selectors that correspond to remove/qty controls (common names)
    const actionableSelectors = [
      '.cart__remove',
      '.js-remove',
      '.cart-item__remove',
      '.remove',
      'button[data-remove-line]',
      'a[href*="/cart/change"]',
      '.cart__qty-plus',
      '.cart__qty-minus',
      '.quantity-increment',
      '.quantity-decrement',
      '.qty-btn',
      'input[name="updates[]"]',
      'input[type="number"]'
    ];

    // if clicked element or its ancestor matches any of those selectors:
    let matched = null;
    for (const sel of actionableSelectors) {
      matched = e.target.closest(sel);
      if (matched) break;
    }
    if (!matched) return;

    // If the matched control is inside a container flagged as free-gift OR has a variant id matching a known gift variant => block it
    const giftContainer = e.target.closest('[data-free-gift], [data-variant-id], [data-product-variant-id], [data-variant], [data-line-variant], [data-cart-item], .cart__row, .cart__line');
    if (!giftContainer) return;

    // find variant id from many possible attributes
    const findVariantIdInNode = (node) => {
      if (!node) return null;
      const attrs = [
        'data-variant-id',
        'data-product-variant-id',
        'data-variant',
        'data-line-variant',
        'data-variantid',
        'data-free-gift-variant'
      ];
      for (const a of attrs) {
        const v = node.getAttribute && node.getAttribute(a);
        if (v) return v;
      }
      // look for input[name="id"] with a value
      const idInput = node.querySelector && node.querySelector('input[name="id"], input[name="variant_id"]');
      if (idInput && idInput.value) return idInput.value;
      return null;
    };

    // climb from matched control up to some container searching for variant id
    let cursor = matched;
    let foundVariant = null;
    for (let i = 0; i < 6 && cursor; i++, cursor = cursor.parentElement) {
      const v = findVariantIdInNode(cursor);
      if (v) { foundVariant = v; break; }
    }

    // also try the giftContainer
    if (!foundVariant) {
      foundVariant = findVariantIdInNode(giftContainer);
    }

    // If foundVariant matches any gift variant id OR giftContainer is marked as free gift, prevent interaction
    if ((foundVariant && this._giftVariantIds.has(String(foundVariant))) || giftContainer.hasAttribute('data-free-gift')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    }
  }

  /**
   * Prevent keyboard edits to quantity inputs that are free gifts.
   * Blocks digit key presses and +/- arrow changes for inputs inside a free-gift container.
   */
  _preventGiftQuantityKeydown(e) {
    if (!this._giftVariantIds || this._giftVariantIds.size === 0) return;

    const input = e.target;
    if (!input || input.tagName !== 'INPUT') return;
    if (!input.matches('input[type="number"], input.qty, input[name="updates[]"]')) return;

    const giftContainer = input.closest('[data-free-gift], [data-free-gift-variant]');
    if (giftContainer) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    }
  }

  /**
   * --- NEW for Option B ---
   * Delegated handler to detect user-initiated clicks that edit the cart.
   * We immediately disable the checkout button here so the user cannot proceed
   * while the edit is in-flight.
   */
  _onUserEditClick(e) {
    // Do nothing if user clicked the checkout button itself
    const checkoutBtn = e.target.closest('.gokwik-checkout, .gokwik-checkout *');
    if (checkoutBtn) return;

    // selectors that correspond to remove or quantity controls (common names)
    const actionableSelectors = [
      '.cart__remove',
      '.js-remove',
      '.cart-item__remove',
      '.remove',
      'button[data-remove-line]',
      'a[href*="/cart/change"]',
      '.cart__qty-plus',
      '.cart__qty-minus',
      '.quantity-increment',
      '.quantity-decrement',
      '.qty-btn',
      '.qty-plus',
      '.qty-minus'
    ];

    for (const sel of actionableSelectors) {
      const matched = e.target.closest(sel);
      if (matched) {
        // If matched element is inside a free gift container, we don't disable (gift interactions are blocked)
        const giftContainer = matched.closest('[data-free-gift], [data-free-gift-variant]');
        if (giftContainer) return;
        // Disable checkout immediately for any user-initiated cart edit click
        this._disableCheckoutButton();
        return;
      }
    }
  }

  /**
   * Delegated handler to detect typing/changes in quantity inputs.
   * We disable checkout as soon as the user types or changes the input.
   */
  _onUserEditInput(e) {
    const input = e.target;
    if (!input) return;

    // ignore inputs outside cart contexts
    if (!input.matches('input[type="number"], input.qty, input.quantity, input[name="updates[]"], input[name^="updates"]')) return;

    // If input is inside a free gift container, do nothing
    const giftContainer = input.closest('[data-free-gift], [data-free-gift-variant]');
    if (giftContainer) return;

    // Some themes may have other guards; still disable checkout on user input
    this._disableCheckoutButton();
  }

  /**
   * Called when cart:updated-and-rendered event fires after the cart UI has finished updating.
   * We re-enable the checkout button here.
   */
  _onCartRendered() {
    this._enableCheckoutButton();
  }

  /**
   * Disable checkout instantly
   */
  _disableCheckoutButton() {
    try {
      const wrapper = document.querySelector('.gokwik-checkout');
      if (!wrapper) return;
      const btn = wrapper.querySelector('button');
      if (!btn) return;
      btn.disabled = true;
      btn.classList.add('disabled-checkout'); // optional CSS hook
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
      // If there's an internal loader element, show it (optional)
      const loader = wrapper.querySelector('#btn-loader');
      if (loader) loader.style.display = 'inline-block';
    } catch (err) {
      // swallow
    }
  }

  /**
   * Re-enable checkout after edit completes
   */
  _enableCheckoutButton() {
    try {
      const wrapper = document.querySelector('.gokwik-checkout');
      if (!wrapper) return;
      const btn = wrapper.querySelector('button');
      if (!btn) return;
      btn.disabled = false;
      btn.classList.remove('disabled-checkout');
      btn.style.pointerEvents = 'auto';
      btn.style.opacity = '1';
      const loader = wrapper.querySelector('#btn-loader');
      if (loader) loader.style.display = 'none';
    } catch (err) {
      // swallow
    }
  }

}

customElements.define("cart-drawer", CartDrawerSection);

/* --- keep your CartDrawerItems as-is (unless you want the same UI protections applied there as well) --- */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
